import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthService, ConfigServerService, CustomHttp } from '../../../../core';
import { ClienteAnaliseMercadoSearchViewCliente } from '../cliente-analise-mercado-search-view-cliente';
import { ClienteAnaliseMercadoSearchCliente } from '../cliente-analise-mercado-search-cliente';
import { ClienteAnaliseMercadoSearchClienteOptions } from '../cliente-analise-mercado-search-cliente-options';
import { ClienteAnaliseMercadoFilter } from '../../adapters';
import { ClienteAuthGuardService } from '../../../pessoa/cliente/services';
import { ClienteCentralCompraAuthGuardService } from '../../../pessoa/cliente/central-compra/services';

@Injectable({
    providedIn: 'root'
})
export class ClienteAnaliseMercadoClienteHttpService {
    private pathApi: string;

    isAdministrador: boolean;
    isAtendente: boolean;
    isRepresentante: boolean;
    isFinanceiro: boolean;

    constructor(private authService: AuthService,
                configServerService: ConfigServerService,
                private customHttp: CustomHttp,
                private clienteAuthGuardService: ClienteAuthGuardService,
                private clienteCentralCompraAuthGuardService: ClienteCentralCompraAuthGuardService) {
        this.pathApi = `${configServerService.getPathApi()}/clienteanalisemercado/cliente`;

        this.isAdministrador = this.authService.isAdministrador();
        this.isAtendente = this.authService.isAtendente();
        this.isRepresentante = this.authService.isRepresentante();
        this.isFinanceiro = this.authService.isFinanceiro();
    }

    private createOptions(cliente: ClienteAnaliseMercadoSearchCliente): ClienteAnaliseMercadoSearchClienteOptions {
        const options = new ClienteAnaliseMercadoSearchClienteOptions();
        if (!cliente) {
            return options;
        }

        /** Análise de Mercado */
        options.canOpenAnaliseMercado = true;

        /** Cliente */
        // Abrir cadastro do cliente
        options.canOpenCliente = true;
        // Pré-baixar cliente
        options.canPreBaixarCliente = this.clienteAuthGuardService.canPreBaixar(cliente.cadastro);
        // Baixar cliente
        options.canBaixarCliente = this.clienteAuthGuardService.canBaixar(cliente.cadastro, cliente.situacao, this.isAdministrador, this.isAtendente, this.isFinanceiro);

        /** Central de Compras */
        /** Inserir uma nova central de compras */
        options.canInsertCentralCompra = true;
        // Editar central de compras
        options.canEditCentralCompra = this.clienteCentralCompraAuthGuardService.canEdit(cliente.tipoPessoa);
        // Inserir participante em uma central de compras
        options.canInsertParticipanteCentralCompra = this.clienteCentralCompraAuthGuardService.canInsertParticipante(cliente.tipoPessoa, cliente.idClienteCentralCompra);

        return options;
    }

    /** Realiza a pesquisa do search view dos clientes nas análises de mercados */
    searchView(filter: ClienteAnaliseMercadoFilter, orderByColumn: number, orderByDir: string,  limit: number): Observable<ClienteAnaliseMercadoSearchViewCliente> {
        let params = new HttpParams();

        // Filter
        if (filter) {
            params = filter.createParams(params);
        }
        // Order By
        params = params.append('orderBy', orderByColumn ? orderByColumn.toString() : null)
                    .append('orderByDir', orderByDir ? orderByDir : null);
        // Limit
        params = params.append('limit', limit ? limit.toString() : null);

        return this.customHttp.get<ClienteAnaliseMercadoSearchViewCliente>(`${this.pathApi}/view`, {params})
                    .pipe(
                        map(searchView => {
                            searchView?.firstClientes?.forEach(cliente => {
                                cliente.options = this.createOptions(cliente);
                            });
                            return searchView;
                        })
                    );
    }

    /** Realiza a pesquisa dos clientes nas análises de mercados */
    search(isdClientes: number[], filter: ClienteAnaliseMercadoFilter, orderByColumn: number, orderByDir: string,  limit: number, offset: number): Observable<ClienteAnaliseMercadoSearchCliente[]> {
        let params = new HttpParams();

        // Filter
        if (filter) {
            params = filter.createParams(params);
        }

        // IDs Clientes
        isdClientes?.forEach(idCliente => params = params.append('idCliente', idCliente.toString()));

        // Order By
        params = params.append('orderBy', orderByColumn ? orderByColumn.toString() : null)
                    .append('orderByDir', orderByDir ? orderByDir : null);
        // Limit
        params = params.append('limit', limit ? limit.toString() : null);
        // Offset
        params = params.append('offset', offset ? offset.toString() : null);

        return this.customHttp.get<ClienteAnaliseMercadoSearchCliente[]>(this.pathApi, {params})
                    .pipe(
                        map(clientes => {
                            clientes?.forEach(cliente => {
                                cliente.options = this.createOptions(cliente);
                            });
                            return clientes;
                        })
                    );
    }

    /** Realiza a pesquisa da análise de mercado do cliente informado */
    searchOne(idCliente: number, filter: ClienteAnaliseMercadoFilter): Observable<ClienteAnaliseMercadoSearchCliente> {
        let params = new HttpParams();

        // Filter
        if (filter) {
            params = filter.createParams(params);
        }

        return this.customHttp.get<ClienteAnaliseMercadoSearchCliente>(`${this.pathApi}/one/${idCliente}`, {params})
                    .pipe(
                        map(cliente => {
                            if (cliente) {
                                cliente.options = this.createOptions(cliente);
                            }

                            return cliente;
                        })
                    );
    }
}
